// Shorts.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import { prepareListPageMeta } from '../resource/prepareListPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import useSSRDataMerge, { resetSSRDataMerge } from '../hook/useSSRDataMerge.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('Shorts loader()');
  const errorLog = legacySSRError.extend('Shorts loader()');
  log('start', { context, params, request });

  // Reset to re-merge SSR data
  resetSSRDataMerge();

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    const state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData });

    const availableLanguages = getSupportedLanguages(state);
    const canonicalPath = '/shorts';
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareListPageMeta({
      state,
      titleKey: 'seo_shorts_title',
      descriptionKey: 'seo_shorts_description',
      keywordsKey: 'seo_cp_post_keywords',
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      jsonLdType: 'WebSite',
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const ShortsRoute = () => {
  const { data, error } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'Shorts',
    loaderData: data,
    loaderError: error,
  });

  return (
    <UniversalComponent
      filename="Container_Shorts"
      importer={/* #__LOADABLE__ */ () => import('../container/Shorts.js')}
    />
  );
};

export default ShortsRoute;
